import { getAccessToken } from '$lib/account.svelte.js';
import { toastDisplay } from '$lib/utils.svelte.js';
import { apiRootUrl, userTags, userDocuments } from '$lib/stores.svelte.js';
import { get } from 'svelte/store';
import { userSession } from '$lib/stores.svelte.js';

/**
 * @typedef {Object} LinkedItem
 * @property {string} id - The associated document's ID
 * @property {string} type - The type of the linked item (e.g. "user_doc")
 */

/**
 * @typedef {Object} Tag
 * @property {string} tag_id - Unique identifier
 * @property {string} user_id - The owner of the tag
 * @property {string} label - The tag text
 * @property {string} updated_at - Last updated time
 * @property {LinkedItem} linked_item - Contains id and type of the linked document
 * @property {Object} [source_content] - Optional standardized representation of the document
 */

/**
 * @description Creates a new tag for multiple items
 * @param {LinkedItem[]} linkedItems - Array of items to tag
 * @param {string} label - The tag text
 * @returns {Promise<string[]>} Array of created tag IDs
 */
export const createTags = async (linkedItems, label) => {
    try {
        const accessToken = await getAccessToken();
        const response = await fetch(`${get(apiRootUrl)}/v3/tag/create/`, {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`
            },
            body: JSON.stringify({
                linked_items: linkedItems,
                label
            })
        });
        const { ids } = await response.json();
        
        // Update local store for each created tag
        const newTags = ids.map((/** @type {string} */ id, /** @type {number} */ index) => ({
            tag_id: id,
            user_id: get(userSession)?.id || '',
            label,
            linked_item: linkedItems[index],
            updated_at: new Date().toISOString()
        }));
        
        userTags.update((/** @type {Tag[]} */ tags) => [...tags, ...newTags]);
        toastDisplay(`Tag(s) added 🏷️`, 'success');
        return ids;
    } catch (e) {
        console.error(e);
        throw new Error('Failed to create tags');
    }
};


/**
 * @description Deletes a tag
 * @param {string} tagId - The ID of the tag to delete
 * @returns {Promise<boolean>}
 */
export const deleteTag = async (tagId) => {
    try {
        const accessToken = await getAccessToken();
        await fetch(`${get(apiRootUrl)}/v3/tag/delete/${tagId}`, {
            method: 'DELETE',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`
            }
        });
        userTags.update((/** @type {Tag[]} */ tags) => tags.filter((/** @type {Tag} */ tag) => tag.tag_id !== tagId));
        return true;
    } catch (e) {
        console.error(e);
        throw new Error('Failed to delete tag');
    }
};

/**
 * @description Deletes all tags with the same label
 * @param {string} label - The label of the tags to delete
 * @returns {Promise<boolean>}
 */
export const deleteAllTagsWithSameLabel = async (label) => {
    try {
        const accessToken = await getAccessToken();
        const tagIds = get(userTags).filter((/** @type {Tag} */ tag) => tag.label === label).map((/** @type {Tag} */ tag) => tag.tag_id);
        await fetch(`${get(apiRootUrl)}/v3/tag/delete/${tagIds.join(',')}`, {
            method: 'DELETE',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`
            }
        });
        userTags.update((/** @type {Tag[]} */ tags) => tags.filter((/** @type {Tag} */ tag) => tag.label !== label));
        userDocuments.update(
            (/** @type {import('$lib/stores.svelte.js').userDocument[]} */ documents) => {
                return documents.map((document) => {
                    const filteredTags = document.tag.filter((doc_tag) => doc_tag.label !== label);
                    return {
                        ...document,
                        tag: filteredTags
                    };
                });
            }
        );
        return true;
    } catch (e) {
        console.error(e);
        throw new Error('Failed to delete tag');
    }
};

/**
 * @description Lists all tags, optionally filtered by source
 * @param {Object} [options]
 * @param {string} [options.source_id] - Filter tags by document ID
 * @param {string} [options.source_type] - Filter by source type
 * @param {boolean} [options.withSources=false] - Whether to include source content
 * @returns {Promise<Tag[]>}
 */
export const listTags = async (options = {}) => {
    try {
        const accessToken = await getAccessToken();
        const queryParams = new URLSearchParams();
        if (options.source_id) queryParams.append('source_id', options.source_id);
        if (options.source_type) queryParams.append('source_type', options.source_type);
        if (options.withSources) queryParams.append('withSources', 'true');
        
        const response = await fetch(`${get(apiRootUrl)}/v3/tag/list/?${queryParams}`, {
            method: 'GET',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`
            }
        });
        const tags = await response.json();
        userTags.set(tags);
        return tags;
    } catch (e) {
        console.error(e);
        return [];
        //throw new Error('Failed to list tags');
    }
};